/**
 * iter-96 — User Support Bot widget.
 *
 * Floating chat button bottom-right of the app shell. Open to ANY user.
 * Backend: POST /api/support/chat → Claude classifies the issue into a
 * non-destructive action that we execute client-side (refresh library,
 * retry playback, resync personas, reset dry-vocal nudge, open help link)
 * OR escalates to a support ticket the owner sees on their dashboard.
 *
 * Designed to feel like Intercom but for fixes, not just answers.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  LifeBuoy, X, Send, Sparkles, Loader2, Mail, RefreshCw, Volume2, Mic2, BadgeAlert,
} from "lucide-react";
import useEscapeClose from "@/lib/useEscapeClose";
import { getUserToken } from "@/lib/userToken";
import { installPlaybackUnlock } from "@/lib/playbackUnlock";
import { safeApiError } from "@/lib/safeErrorText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Recording / mixing screens keep the corner free — the floating button sat on top of the controls on phones.
const RECORDING_PAGE = /^\/create\/(project\/[^/]+\/(sing|replace)|song\/[^/]+\/stems)/;

const EXAMPLE_PROMPTS = [
  "My songs won't play",
  "My library is empty",
  "Where are my voices?",
  "How do I turn off Dry Vocal?",
];

const ACTION_ICON = {
  refresh_library: RefreshCw,
  retry_playback: Volume2,
  resync_personas: Mic2,
  reset_dry_vocal_nudge: Sparkles,
  report_issue: BadgeAlert,
  open_help_link: Sparkles,
  none: Sparkles,
};

/**
 * Apply an action client-side. Returns a human-readable result string.
 */
function applyAction(action, helpUrl) {
  switch (action) {
    case "refresh_library":
      try {
        window.dispatchEvent(new CustomEvent("elpm:refresh-library"));
      } catch (e) { void e; }
      return "Library refreshed — open My Songs to see your latest tracks.";
    case "retry_playback":
      installPlaybackUnlock();
      try {
        // Surface a confirmation toast that prompts the user to tap once.
        toast.info("Tap anywhere on the page once — playback should work right after.", { duration: 6000 });
      } catch (e) { void e; }
      return "Tap the page once to unlock playback.";
    case "resync_personas":
      try {
        window.dispatchEvent(new CustomEvent("elpm:refresh-personas"));
      } catch (e) { void e; }
      return "Re-fetched your trained voices.";
    case "reset_dry_vocal_nudge":
      try {
        sessionStorage.removeItem("elpm-dry-vocal-nudged");
        localStorage.setItem("elpm-suno-dry-vocal", "0");
      } catch (e) { void e; }
      return "Dry Vocal turned off — open Make Music to confirm.";
    case "open_help_link":
      if (helpUrl) {
        if (helpUrl.startsWith("#")) {
          // Same-page anchor.
          try { window.location.hash = helpUrl; } catch (e) { void e; }
        } else if (/^https?:\/\//i.test(helpUrl)) {
          try { window.open(helpUrl, "_blank", "noopener,noreferrer"); } catch (e) { void e; }
        }
      }
      return "Opening the help section…";
    case "report_issue":
      return "Filed a ticket — the owner will follow up.";
    default:
      return "";
  }
}

export default function SupportBotWidget() {
  const { pathname } = useLocation();
  const recordingPage = RECORDING_PAGE.test(pathname);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [thinking, setThinking] = useState(false);
  const endRef = useRef(null);
  useEscapeClose(open, () => setOpen(false));

  useEffect(() => {
    if (open) setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [open, history]);

  // iter-97 — Auto-error-detector dispatches this event when a known issue
  // pattern is detected (e.g. audio NotAllowedError). The widget opens
  // pre-loaded with the suggested message so the user just hits Send.
  useEffect(() => {
    const handler = (e) => {
      const msg = e?.detail?.message;
      if (typeof msg === "string" && msg.trim()) setMessage(msg);
      setOpen(true);
    };
    window.addEventListener("elpm:open-support-bot", handler);
    return () => window.removeEventListener("elpm:open-support-bot", handler);
  }, []);

  const send = async () => {
    const txt = message.trim();
    if (!txt) return;
    setMessage("");
    setHistory((h) => [...h, { role: "user", content: txt }]);
    setThinking(true);
    try {
      const { data } = await axios.post(`${API}/support/chat`, {
        message: txt,
        user_token: getUserToken(),
        context: { page: window.location.pathname, ua: navigator.userAgent.slice(0, 120) },
      });
      const Icon = ACTION_ICON[data.action] || Sparkles;
      const applied = applyAction(data.action, data.help_url);
      setHistory((h) => [...h, {
        role: "bot",
        content: data.narration,
        action: data.action,
        ticketFiled: !!data.ticket_filed,
        applied,
        Icon,
      }]);
    } catch (e) {
      const detail = safeApiError(e, "Couldn't reach support");
      setHistory((h) => [...h, { role: "bot", content: `Error: ${detail}`, isError: true }]);
    } finally {
      setThinking(false);
    }
  };

  return (
    <>
      {/* Floating button — positioned bottom-right but lifted ABOVE the
          "Made with Emergent" badge so it remains tappable in preview AND
          production. */}
      <button
        type="button"
        data-testid="support-bot-trigger"
        onClick={() => setOpen(true)}
        title="Need help? Open the support bot"
        className={`fixed bottom-20 right-5 z-[90] flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-fuchsia-500/85 to-rose-500/80 text-white shadow-xl shadow-fuchsia-900/40 ring-1 ring-fuchsia-300/40 transition hover:from-fuchsia-500 hover:to-rose-500 ${recordingPage ? "hidden" : ""} ${open ? "pointer-events-none scale-95 opacity-0" : "scale-100 opacity-100"}`}
      >
        <LifeBuoy className="h-5 w-5" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
          key="supportbotwidget-overlay"
            className="fixed inset-0 z-[120] flex items-end justify-end bg-black/0 sm:bg-black/40 sm:backdrop-blur-[2px]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="support-bot-modal"
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", stiffness: 240, damping: 24 }}
              className="mb-20 mr-3 ml-3 mt-3 flex w-full max-w-md flex-col rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-900 to-zinc-950 shadow-2xl"
              style={{ height: "min(72vh, 580px)" }}
            >
              <header className="flex items-center justify-between border-b border-white/8 px-5 py-3">
                <div className="flex items-center gap-2.5">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-fuchsia-500/80 to-rose-500/70">
                    <LifeBuoy className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold tracking-tight text-white">Support</h3>
                    <p className="text-[10px] text-white/55">Auto-fixes most issues · escalates the rest</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  data-testid="support-bot-close"
                  className="rounded-full bg-white/5 p-1.5 text-white/70 transition hover:bg-white/12 hover:text-white"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </header>

              <div className="flex-1 space-y-3 overflow-y-auto px-5 py-3.5">
                {history.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-white/12 bg-white/[0.04] p-3 text-sm text-white/65">
                    <div className="mb-2 flex items-center gap-1.5 text-fuchsia-200/85">
                      <Sparkles className="h-3.5 w-3.5" />
                      <span className="font-mono text-[10px] uppercase tracking-wider">Common issues</span>
                    </div>
                    <div className="space-y-1">
                      {EXAMPLE_PROMPTS.map((p, idx) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setMessage(p)}
                          className="block w-full rounded-lg px-2 py-1.5 text-left text-[12.5px] text-white/75 transition hover:bg-white/[0.06] hover:text-white"
                          data-testid={`support-bot-example-${idx}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {history.map((row, idx) => (
                  <div key={`${idx}-${row.role}-${(row.content || "").slice(0, 24)}`} className={row.role === "user" ? "flex justify-end" : "flex justify-start"}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-[13px] ${
                        row.role === "user"
                          ? "bg-fuchsia-500/22 text-white ring-1 ring-fuchsia-400/30"
                          : row.isError
                          ? "bg-rose-500/14 text-rose-100 ring-1 ring-rose-400/30"
                          : "bg-white/[0.06] text-white/90 ring-1 ring-white/10"
                      }`}
                    >
                      {row.role === "bot" && row.Icon && (
                        <div className="mb-1 flex items-center gap-1.5 text-fuchsia-200/75">
                          <row.Icon className="h-3 w-3" />
                          <span className="font-mono text-[9px] uppercase tracking-wider">
                            {(row.action || "info").replace(/_/g, " ")}
                          </span>
                        </div>
                      )}
                      <p className="whitespace-pre-wrap">{row.content}</p>
                      {row.applied && (
                        <p className="mt-1 text-[11px] text-emerald-200/80">{row.applied}</p>
                      )}
                      {row.ticketFiled && (
                        <p className="mt-1 text-[11px] text-amber-200/80">Ticket filed for owner review.</p>
                      )}
                    </div>
                  </div>
                ))}
                {thinking && (
                  <div className="flex justify-start">
                    <div className="flex items-center gap-2 rounded-2xl bg-white/[0.06] px-3.5 py-2 text-[13px] text-white/65 ring-1 ring-white/10">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>Thinking…</span>
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              <footer className="border-t border-white/8 p-3.5">
                <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 px-3 py-2">
                  <Mail className="h-3.5 w-3.5 text-white/45" />
                  <input
                    data-testid="support-bot-input"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Describe what's not working…"
                    className="flex-1 bg-transparent text-[13px] text-white outline-none placeholder:text-white/30"
                  />
                  <button
                    type="button"
                    data-testid="support-bot-send"
                    onClick={send}
                    disabled={thinking || !message.trim()}
                    className="flex items-center gap-1 rounded-lg bg-fuchsia-500/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-fuchsia-50 ring-1 ring-fuchsia-400/40 transition hover:bg-fuchsia-500/45 disabled:opacity-40"
                  >
                    {thinking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    Send
                  </button>
                </div>
              </footer>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
